import React, { useState, useRef } from 'react';
import { X, CreditCard, FileText, DollarSign, Smartphone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Order, Payment } from '../../services/dataService';
import PrintReceiptButton from './PrintReceiptButton';
import WhatsAppReceiptButton from './WhatsAppReceiptButton';
import { createCheckoutIdempotencyKey } from '../../utils/checkoutIdempotency';

interface PaymentModalProps {
  order: Order;
  onClose: () => void;
  onSuccess: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ order, onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submitInFlightRef = useRef(false);
  
  const [formData, setFormData] = useState({
    amount: order.remainingAmount,
    method: 'cash' as Payment['method'],
    notes: ''
  });

  const paymentMethods = [
    { value: 'cash', label: 'Cash', icon: DollarSign, color: 'green' },
    { value: 'card', label: 'Card', icon: CreditCard, color: 'blue' },
    { value: 'digital', label: 'Digital Wallet', icon: Smartphone, color: 'purple' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitInFlightRef.current || loading) {
      return;
    }
    
    if (formData.amount <= 0) {
      setError('Payment amount must be greater than 0');
      return;
    }

    if (formData.amount > order.remainingAmount) {
      setError(`Payment amount cannot exceed remaining balance of ₹${order.remainingAmount.toFixed(2)}`);
      return;
    }

    const idempotencyKey = createCheckoutIdempotencyKey('payment');
    submitInFlightRef.current = true;

    try {
      setLoading(true);
      setError('');

      const notesToPass = formData.notes.trim();
      const paymentOptions = { idempotencyKey };
      
      if (notesToPass) {
        await dataService.addPaymentToOrder(
          tenantData!.id,
          order.id!,
          formData.amount,
          formData.method,
          notesToPass,
          paymentOptions
        );
      } else {
        await dataService.addPaymentToOrder(
          tenantData!.id,
          order.id!,
          formData.amount,
          formData.method,
          undefined,
          paymentOptions
        );
      }

      // Close modal immediately - real-time subscriptions will handle data updates
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error processing payment:', error);
      setError('Failed to process payment. Please try again.');
      setLoading(false);
      submitInFlightRef.current = false;
    }
  };

  const isFullPayment = formData.amount >= order.remainingAmount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg md:max-w-4xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 dark:bg-green-900/30 p-1.5 rounded-lg">
              <CreditCard className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Add Payment</h2>
              <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                <span>{order.customer}</span>
                {order.customerPhone && <span>{order.customerPhone}</span>}
                {order.tableNumber && <span>Table {order.tableNumber}</span>}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                <p className="text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Two Column Layout for Tablets+ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column - Order Details */}
            <div className="space-y-4">
              {/* Order Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Order Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-300">Total Amount:</span>
                    <span className="font-semibold dark:text-white">₹{order.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-300">Paid Amount:</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">₹{order.paidAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-300">Remaining Balance:</span>
                    <span className="font-bold text-red-600 dark:text-red-400">₹{order.remainingAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Order Items</h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{item.menuItemName}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">₹{item.unitPrice} × {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold dark:text-white">₹{item.totalPrice.toFixed(2)}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          item.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                          item.status === 'preparing' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Payment Form */}
            <div className="space-y-4">

              {/* Payment Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Amount *
                </label>
                <div className="relative max-w-xs">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400 dark:text-gray-500 text-lg font-medium">₹</span>
                  </div>
                  <input
                    type="number"
                    name="amount"
                    required
                    min="0.01"
                    max={order.remainingAmount}
                    step="0.01"
                    value={formData.amount}
                    onChange={handleInputChange}
                    className="w-full px-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Method *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    const isSelected = formData.method === method.value;
                    
                    // Color schemes for each payment method
                    const colorSchemes = {
                      green: {
                        selected: 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
                        unselected: 'border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-500 text-gray-700 dark:text-gray-300'
                      },
                      blue: {
                        selected: 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
                        unselected: 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 text-gray-700 dark:text-gray-300'
                      },
                      purple: {
                        selected: 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
                        unselected: 'border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 text-gray-700 dark:text-gray-300'
                      }
                    };

                    const scheme = colorSchemes[method.color as keyof typeof colorSchemes];
                    const className = isSelected ? scheme.selected : scheme.unselected;

                    return (
                      <label
                        key={method.value}
                        className={`relative flex items-center justify-center p-2 border rounded cursor-pointer transition-colors duration-200 ${className}`}
                      >
                        <input
                          type="radio"
                          name="method"
                          value={method.value}
                          checked={isSelected}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="flex flex-col items-center space-y-1">
                          <Icon className="w-4 h-4" />
                          <span className="text-xs font-medium">{method.label}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Payment Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent resize-none"
                  placeholder="Optional payment notes"
                />
              </div>

              {/* Payment Summary */}
              {formData.amount > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">Payment Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 dark:text-blue-400">Payment Amount:</span>
                      <span className="font-semibold text-blue-900 dark:text-blue-300">₹{formData.amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 dark:text-blue-400">Payment Method:</span>
                      <span className="font-medium text-blue-900 dark:text-blue-300 capitalize">{formData.method}</span>
                    </div>
                    <hr className="border-blue-200 dark:border-blue-700" />
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 dark:text-blue-400">Remaining After Payment:</span>
                      <span className={`font-bold text-lg ${
                        order.remainingAmount - formData.amount <= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        ₹{(order.remainingAmount - formData.amount).toFixed(2)}
                      </span>
                    </div>
                    {order.remainingAmount - formData.amount <= 0 && (
                      <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded p-2 mt-2">
                        <p className="text-green-800 dark:text-green-300 text-sm font-medium">✅ This payment will complete the order</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>

          {/* Fixed Action Buttons */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
            <div className="flex flex-col space-y-2">
            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || formData.amount <= 0}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : isFullPayment ? 'Complete Order Payment' : 'Process Partial Payment'}
              </button>
              </div>
              
              {/* Receipt Options */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">Receipt Options</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Print Receipt Button */}
                  <PrintReceiptButton
                    order={order}
                    variant="secondary"
                    size="md"
                    className="w-full"
                    onPrintError={(error) => console.error('Print error:', error)}
                    onPrintSuccess={() => console.log('Receipt printed successfully')}
                  />
                  
                  {/* WhatsApp Receipt Button */}
                  <div className="w-full">
                    <WhatsAppReceiptButton
                      order={order}
                      cafeName="CafeLawn"
                      cafeAddress="Your Cafe Address"
                      cafePhone="Your Cafe Phone"
                      cafeInstagram="@cafelawn"
                      onPhoneUpdate={async (orderId: string, phone: string) => {
                        try {
                          // Update customer phone in the order
                          await dataService.update<Order>(tenantData!.id, 'orders', orderId, {
                            customerPhone: phone
                          });
                          // You might want to trigger a refresh here or handle the update
                        } catch (error) {
                          console.error('Error updating phone:', error);
                          throw error;
                        }
                      }}
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                  Choose how you'd like to share the receipt with your customer
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal; 